import { v4 as uuidv4 } from 'uuid';
import { getDB } from './db';

// Listeners for realtime local updates across components
const listeners = new Set();
const notifyListeners = () => listeners.forEach(fn => fn());

export const api = {
  subscribeToRegistrations(eventId, callback) {
    listeners.add(callback);
    return () => listeners.delete(callback);
  },

  async testConnection() {
    try {
      const db = await getDB();
      return !!db;
    } catch (e) {
      throw new Error('IndexedDB is not available or blocked in this browser.');
    }
  },

  async getOrCreateMidsummerEvent() {
    const db = await getDB();
    const midsummerEventId = 'evt-midsummer-2026';
    let event = await db.get('events', midsummerEventId);
    
    if (!event) {
      event = {
        event_id: midsummerEventId,
        event_name: 'The Padua Midsummer Affair',
        event_date: '2026-08-23',
        start_time: '11:00:00',
        end_time: '15:00:00',
        location: 'Claremont, CA',
        status: 'Upcoming',
        created_at: new Date().toISOString()
      };
      await db.put('events', event);
    }
    return event;
  },

  async getAllContacts() {
    const db = await getDB();
    return await db.getAll('contacts');
  },

  async getGuests(eventId) {
    if (!eventId) return [];
    const db = await getDB();
    const regs = await db.getAllFromIndex('registrations', 'event_id', eventId);
    
    // Join contacts & relationships
    const guests = [];
    for (const reg of regs) {
      const contact = await db.get('contacts', reg.contact_id) || {};
      const relationship = await db.get('relationships', reg.contact_id) || {};
      guests.push({ ...reg, contact, relationship });
    }
    
    return guests.sort((a, b) => (a.contact.last_name || '').localeCompare(b.contact.last_name || ''));
  },

  async searchGuests(eventId, query) {
    const guests = await this.getGuests(eventId);
    if (!query) return guests;
    
    const q = query.toLowerCase();
    return guests.filter(g => 
      g.contact.first_name?.toLowerCase().includes(q) || 
      g.contact.last_name?.toLowerCase().includes(q) ||
      g.contact.email?.toLowerCase().includes(q) ||
      g.contact.phone?.includes(q)
    );
  },

  async checkInGuest(registrationId, updates, activityInfo) {
    const db = await getDB();
    const reg = await db.get('registrations', registrationId);
    
    if (!reg) return false;
    
    reg.checked_in = true;
    reg.check_in_time = new Date().toISOString();
    reg.registration_status = 'Checked In';
    reg.checked_in_by = activityInfo.staffName;
    reg.updated_at = new Date().toISOString();
    
    if (updates.arrived_guest_count !== undefined) {
      reg.arrived_guest_count = updates.arrived_guest_count;
    }
    
    await db.put('registrations', reg);
    
    if (updates.relationship) {
      const existingRel = await db.get('relationships', reg.contact_id) || { contact_id: reg.contact_id };
      await db.put('relationships', {
        ...existingRel,
        ...updates.relationship,
        last_updated: new Date().toISOString()
      });
    }
    
    await db.put('activity', {
      activity_id: uuidv4(),
      contact_id: reg.contact_id,
      event_id: reg.event_id,
      activity_type: 'Checked In',
      performed_by: activityInfo.staffName,
      notes: activityInfo.notes || '',
      activity_timestamp: new Date().toISOString()
    });
    
    notifyListeners();
    return true;
  },

  async undoCheckIn(registrationId, staffName) {
    const db = await getDB();
    const reg = await db.get('registrations', registrationId);
    
    if (!reg) return false;
    
    reg.checked_in = false;
    reg.check_in_time = null;
    reg.checked_in_by = null;
    reg.registration_status = 'RSVP';
    reg.updated_at = new Date().toISOString();
    
    await db.put('registrations', reg);
    
    await db.put('activity', {
      activity_id: uuidv4(),
      contact_id: reg.contact_id,
      event_id: reg.event_id,
      activity_type: 'Check-In Reset',
      performed_by: staffName,
      notes: '',
      activity_timestamp: new Date().toISOString()
    });
    
    notifyListeners();
    return true;
  },

  async resetAllCheckIns(eventId, staffName) {
    const db = await getDB();
    const regs = await db.getAllFromIndex('registrations', 'event_id', eventId);
    let count = 0;
    
    for (const reg of regs) {
      if (reg.checked_in) {
        reg.checked_in = false;
        reg.check_in_time = null;
        reg.checked_in_by = null;
        reg.registration_status = 'RSVP';
        reg.updated_at = new Date().toISOString();
        
        await db.put('registrations', reg);
        count++;
        
        await db.put('activity', {
          activity_id: uuidv4(),
          contact_id: reg.contact_id,
          event_id: eventId,
          activity_type: 'Check-In Reset',
          performed_by: staffName,
          notes: 'Bulk Reset',
          activity_timestamp: new Date().toISOString()
        });
      }
    }
    
    notifyListeners();
    return count;
  },

  async addWalkIn(event_id, contactData, relationshipData, staffName) {
    const db = await getDB();
    
    const normEmail = contactData.email?.trim().toLowerCase();
    const normPhone = contactData.phone?.replace(/\D/g, '');
    
    let contact = null;
    if (normEmail) contact = await db.getFromIndex('contacts', 'email', normEmail);
    if (!contact && normPhone) contact = await db.getFromIndex('contacts', 'phone', normPhone);
    
    if (!contact) {
      contact = {
        contact_id: uuidv4(),
        first_name: contactData.first_name?.trim(),
        last_name: contactData.last_name?.trim(),
        email: normEmail || '',
        phone: normPhone || '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      await db.put('contacts', contact);
    }
    
    const existingRel = await db.get('relationships', contact.contact_id);
    if (!existingRel) {
      await db.put('relationships', {
        contact_id: contact.contact_id,
        ...relationshipData,
        created_at: new Date().toISOString(),
        last_updated: new Date().toISOString()
      });
    }
    
    const reg = {
      registration_id: uuidv4(),
      contact_id: contact.contact_id,
      event_id,
      rsvp_source: 'Walk-In',
      guest_count: 1,
      arrived_guest_count: 1,
      registration_status: 'Checked In',
      checked_in: true,
      check_in_time: new Date().toISOString(),
      checked_in_by: staffName,
      walk_in: true,
      notes: '',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };
    
    await db.put('registrations', reg);
    
    await db.put('activity', {
      activity_id: uuidv4(),
      contact_id: contact.contact_id,
      event_id,
      activity_type: 'Walk-In Created',
      performed_by: staffName,
      notes: '',
      activity_timestamp: new Date().toISOString()
    });
    
    notifyListeners();
    return true;
  },

  async importBatch(eventId, rows, staffName, fileName, sourceFormat, onProgress) {
    const db = await getDB();
    const stats = {
      total_rows: rows.length,
      new_contacts: 0,
      existing_contacts: 0,
      skipped_rows: 0,
      errors: 0
    };

    let processedCount = 0;

    for (const row of rows) {
      processedCount++;
      if (processedCount % 10 === 0 && onProgress) {
        onProgress({ current: processedCount, total: rows.length, stage: 'Processing records...' });
        await new Promise(r => setTimeout(r, 0));
      }

      const fName = row.first_name?.trim();
      const lName = row.last_name?.trim();

      if (!fName || !lName) {
        stats.skipped_rows++;
        continue;
      }

      const normEmail = row.email?.trim().toLowerCase() || '';
      const normPhone = row.phone?.replace(/\D/g, '') || '';

      let contact = null;
      if (normEmail) contact = await db.getFromIndex('contacts', 'email', normEmail);
      if (!contact && normPhone) contact = await db.getFromIndex('contacts', 'phone', normPhone);

      if (!contact) {
        contact = {
          contact_id: uuidv4(),
          first_name: fName,
          last_name: lName,
          email: normEmail,
          phone: normPhone,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        await db.put('contacts', contact);
        stats.new_contacts++;
      } else {
        let updated = false;
        if (!contact.first_name && fName) { contact.first_name = fName; updated = true; }
        if (!contact.last_name && lName) { contact.last_name = lName; updated = true; }
        if (!contact.email && normEmail) { contact.email = normEmail; updated = true; }
        if (!contact.phone && normPhone) { contact.phone = normPhone; updated = true; }
        
        if (updated) {
          contact.updated_at = new Date().toISOString();
          await db.put('contacts', contact);
        }
        stats.existing_contacts++;
      }

      let safeWeddingDate = null;
      if (row.wedding_date) {
        const d = new Date(row.wedding_date);
        if (!isNaN(d.getTime())) {
          safeWeddingDate = d.toISOString().split('T')[0];
        }
      }

      let safeRsvpDate = new Date().toISOString();
      if (row.rsvp_date) {
        const d = new Date(row.rsvp_date);
        if (!isNaN(d.getTime())) {
          safeRsvpDate = d.toISOString();
        }
      }

      const existingRel = await db.get('relationships', contact.contact_id);
      
      if (!existingRel) {
        await db.put('relationships', {
          contact_id: contact.contact_id,
          relationship_type: 'Guest / Other',
          wedding_date: safeWeddingDate,
          partner_name: row.partner_name || null,
          created_at: new Date().toISOString(),
          last_updated: new Date().toISOString()
        });
      } else {
        let relUpdated = false;
        if (safeWeddingDate && !existingRel.wedding_date) {
          existingRel.wedding_date = safeWeddingDate;
          relUpdated = true;
        }
        if (row.partner_name && !existingRel.partner_name) {
          existingRel.partner_name = row.partner_name;
          relUpdated = true;
        }
        if (relUpdated) {
          existingRel.last_updated = new Date().toISOString();
          await db.put('relationships', existingRel);
        }
      }

      const rsvpSource = row.rsvp_source || sourceFormat || 'CSV Import';
      const allRegs = await db.getAllFromIndex('registrations', 'contact_id', contact.contact_id);
      const existingReg = allRegs.find(r => r.event_id === eventId);

      if (!existingReg) {
        await db.put('registrations', {
          registration_id: uuidv4(),
          contact_id: contact.contact_id,
          event_id: eventId,
          rsvp_source: rsvpSource,
          rsvp_date: safeRsvpDate,
          import_filename: fileName,
          guest_count: parseInt(row.guest_count) || 1,
          arrived_guest_count: 0,
          registration_status: 'Registered',
          checked_in: false,
          walk_in: false,
          notes: row.notes || '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
      } else {
        let regUpdated = false;
        if (existingReg.rsvp_source !== rsvpSource && !existingReg.rsvp_source.includes(rsvpSource)) {
          existingReg.rsvp_source = `${existingReg.rsvp_source} + ${rsvpSource}`;
          regUpdated = true;
        }
        if (!existingReg.import_filename && fileName) {
          existingReg.import_filename = fileName;
          regUpdated = true;
        }
        if (regUpdated) {
          existingReg.updated_at = new Date().toISOString();
          await db.put('registrations', existingReg);
        }
      }
    }

    await db.put('activity', {
      activity_id: uuidv4(),
      contact_id: null,
      event_id: eventId,
      activity_type: 'CSV Import',
      performed_by: staffName,
      notes: `Imported ${rows.length} rows from ${fileName}`,
      activity_timestamp: new Date().toISOString()
    });

    notifyListeners();
    return stats;
  },

  async getDashboardStats(eventId) {
    const guests = await this.getGuests(eventId);
    
    // Parties
    const totalRSVPs = guests.length;
    const checkedInGuestsList = guests.filter(g => g.checked_in);
    const checkedIn = checkedInGuestsList.length;
    const notArrived = totalRSVPs - checkedIn;
    const walkIns = guests.filter(g => g.walk_in).length;
    
    // Headcounts
    const expectedHeadcount = guests.reduce((sum, g) => sum + (Number(g.guest_count) || 1), 0);
    const totalHeadcount = checkedInGuestsList.reduce((sum, g) => {
      return sum + (Number(g.arrived_guest_count) || 1);
    }, 0);

    const attendanceRate = totalRSVPs > 0 ? Math.round((checkedIn / totalRSVPs) * 100) : 0;
    
    // Audience Breakdown - Filtered by checkedInGuestsList
    const booked = checkedInGuestsList.filter(g => g.relationship?.relationship_type === 'Booked Couple').length;
    const prospective = checkedInGuestsList.filter(g => g.relationship?.relationship_type === 'Prospective Couple').length;
    const other = checkedIn - booked - prospective;

    // Tour Status - Filtered by checkedInGuestsList
    const tourNever = checkedInGuestsList.filter(g => g.relationship?.tour_status === 'Never Toured').length;
    const tourScheduled = checkedInGuestsList.filter(g => g.relationship?.tour_status === 'Tour Scheduled').length;
    const tourPreviously = checkedInGuestsList.filter(g => g.relationship?.tour_status === 'Previously Toured').length;
    const tourUnknown = checkedInGuestsList.filter(g => g.relationship?.tour_status === 'Unknown').length;

    return {
      totalRSVPs,
      expectedHeadcount,
      checkedIn,
      totalHeadcount,
      notArrived,
      walkIns,
      attendanceRate,
      booked,
      prospective,
      other,
      tourNever,
      tourScheduled,
      tourPreviously,
      tourUnknown
    };
  },

  async getAudienceSummaryStats(eventId) {
    const guests = await this.getGuests(eventId);
    
    const total = guests.length;
    let checkedIn = 0;
    let notCheckedIn = 0;
    let booked = 0;
    let prospective = 0;
    let neverToured = 0;
    let previouslyToured = 0;
    let tourScheduled = 0;
    let walkIns = 0;
    let guestsFamilyOther = 0;
    let vendors = 0;

    guests.forEach(g => {
      if (g.checked_in) checkedIn++;
      else notCheckedIn++;

      if (g.walk_in) walkIns++;

      const rel = g.relationship?.relationship_type;
      const tour = g.relationship?.tour_status;

      if (rel === 'Booked Couple') booked++;
      else if (rel === 'Prospective Couple') {
        prospective++;
        if (tour === 'Never Toured') neverToured++;
        else if (tour === 'Previously Toured') previouslyToured++;
        else if (tour === 'Tour Scheduled') tourScheduled++;
      }
      else if (rel === 'Vendor') vendors++;
      else if (rel === 'Guest / Family / Friend' || rel === 'Guest / Other') guestsFamilyOther++;
      else guestsFamilyOther++;
    });

    return {
      total,
      checkedIn,
      notCheckedIn,
      booked,
      prospective,
      neverToured,
      previouslyToured,
      tourScheduled,
      walkIns,
      guestsFamilyOther,
      vendors
    };
  },

  async getFullBackup() {
    const db = await getDB();
    const contacts = await db.getAll('contacts');
    const events = await db.getAll('events');
    const registrations = await db.getAll('registrations');
    const relationships = await db.getAll('relationships');
    const activity = await db.getAll('activity');
    const imports = await db.getAll('imports');

    return {
      backup_metadata: {
        backup_created_at: new Date().toISOString(),
        total_contacts: contacts.length,
        total_registrations: registrations.length,
      },
      contacts,
      events,
      registrations,
      relationships,
      activity,
      imports
    };
  },

  async restoreBackup(backupData) {
    const db = await getDB();
    
    await db.clear('contacts');
    await db.clear('events');
    await db.clear('registrations');
    await db.clear('relationships');
    await db.clear('activity');
    await db.clear('imports');

    if (backupData.contacts) for (const item of backupData.contacts) await db.put('contacts', item);
    if (backupData.events) for (const item of backupData.events) await db.put('events', item);
    if (backupData.registrations) for (const item of backupData.registrations) await db.put('registrations', item);
    if (backupData.relationships) for (const item of backupData.relationships) await db.put('relationships', item);
    if (backupData.activity) for (const item of backupData.activity) await db.put('activity', item);
    if (backupData.imports) for (const item of backupData.imports) await db.put('imports', item);

    notifyListeners();
    return true;
  },

  async dangerousDeleteAllData() {
    const db = await getDB();
    await db.clear('contacts');
    await db.clear('events');
    await db.clear('registrations');
    await db.clear('relationships');
    await db.clear('activity');
    await db.clear('imports');
    notifyListeners();
  },

  async getGuestListCsvData(eventId, filterType = 'All') {
    let guests = await this.getGuests(eventId);

    if (filterType === 'Checked-In Guests') guests = guests.filter(g => g.checked_in);
    else if (filterType === 'No-Shows') guests = guests.filter(g => !g.checked_in);
    else if (filterType === 'Booked Couples') guests = guests.filter(g => g.relationship?.relationship_type === 'Booked Couple');
    else if (filterType === 'Prospective Couples') guests = guests.filter(g => g.relationship?.relationship_type === 'Prospective Couple');
    else if (filterType === 'Never-Toured Prospects') guests = guests.filter(g => g.relationship?.relationship_type === 'Prospective Couple' && g.relationship?.tour_status === 'Never Toured');
    else if (filterType === 'Previously-Toured Prospects') guests = guests.filter(g => g.relationship?.relationship_type === 'Prospective Couple' && g.relationship?.tour_status === 'Previously Toured');
    else if (filterType === 'Walk-Ins') guests = guests.filter(g => g.walk_in);

    const clean = (val) => {
      if (val === null || val === undefined || Number.isNaN(val) || val === 'undefined' || val === 'null') return '';
      return val;
    };

    return guests.map(g => {
      const rel = g.relationship?.relationship_type || 'Unknown';
      const tour = g.relationship?.tour_status || 'Unknown';
      const checkedIn = g.checked_in;
      const walkIn = g.walk_in;

      let segment = 'Other';
      let followUp = 'No Follow-Up Required';

      if (rel === 'Booked Couple') segment = 'Booked Padua Couple';
      else if (rel === 'Prospective Couple') {
        if (walkIn) segment = 'Walk-In Prospect';
        else segment = `Prospective Couple — ${tour}`;
      }
      else if (rel === 'Guest / Family / Friend' || rel === 'Guest / Other') segment = 'Guest / Family / Friend';
      else if (rel === 'Vendor') segment = 'Vendor';
      else if (!checkedIn) segment = 'RSVP — No Show';
      else segment = 'Other';

      if (!checkedIn) {
        followUp = 'No-Show Follow-Up';
      } else if (walkIn && rel === 'Prospective Couple') {
        followUp = 'Walk-In Lead Follow-Up';
      } else if (rel === 'Booked Couple') {
        followUp = 'Booked Couple Communication';
      } else if (rel === 'Prospective Couple') {
        if (tour === 'Never Toured') followUp = 'Private Tour Follow-Up';
        else if (tour === 'Previously Toured' || tour === 'Tour Scheduled') followUp = 'Sales Follow-Up';
        else followUp = 'Private Tour Follow-Up';
      }

      return {
        'First Name': clean(g.contact?.first_name),
        'Last Name': clean(g.contact?.last_name),
        'Email': clean(g.contact?.email),
        'Phone': clean(g.contact?.phone),
        'Partner Name': clean(g.relationship?.partner_name),
        'RSVP Source': clean(g.rsvp_source),
        'RSVP Date': clean(g.rsvp_date ? new Date(g.rsvp_date).toLocaleString() : ''),
        'Original Import File': clean(g.import_filename),
        'Import Date': clean(g.created_at ? new Date(g.created_at).toLocaleString() : ''),
        'Attendance Status': checkedIn ? 'Checked In' : 'Not Checked In',
        'Checked In': checkedIn ? 'Yes' : 'No',
        'Party Size': clean(g.arrived_guest_count),
        'Check-In Time': clean(g.check_in_time ? new Date(g.check_in_time).toLocaleString() : ''),
        'Checked-In By': clean(g.checked_in_by),
        'Relationship Type': clean(rel),
        'Booked at Padua': g.relationship?.booked_at_padua ? 'Yes' : 'No',
        'Wedding Date': clean(g.relationship?.wedding_date),
        'Tour Status': clean(tour),
        'Lead Status': clean(g.relationship?.lead_status),
        'Sales Advisor': clean(g.relationship?.sales_advisor),
        'Walk-In': walkIn ? 'Yes' : 'No',
        'Audience Segment': segment,
        'Follow-Up Opportunity': followUp,
        'Staff Notes': clean(g.notes),
        'Created At': clean(g.created_at ? new Date(g.created_at).toLocaleString() : ''),
        'Last Updated': clean(g.updated_at ? new Date(g.updated_at).toLocaleString() : '')
      };
    });
  }
};